import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useBotWorkspace } from "../../../components/admin/BotWorkspace";
import { AlertCircle, CalendarDays, CalendarPlus, Clock3, Download, ExternalLink, Eye, FileSignature, FileText, MessageCircle, Pencil, UserRound, UsersRound, X } from "lucide-react";
import { listBusinesses } from "../../../api/chatbotApi";
import { fetchTenantWhatsappConversations, fetchTenantWhatsappMessages, fetchWhatsAppConversationDetails } from "../../../api/centralWhatsappApi";
import { getFormDocument, getFormsErrorMessage, listFormRequests, previewFormRequest } from "../../../api/formsApi";
import { getCustomer, getSchedulingErrorMessage, getSchedulingSettings, listProviders, listServices } from "../../../api/schedulingApi";
import CustomersPanel from "../../../components/admin/scheduling/CustomersPanel";
import ResizableCustomerLayout from "../../../components/admin/scheduling/ResizableCustomerLayout";
import AppointmentsPanel, { AppointmentActions, NewAppointmentModal } from "../../../components/admin/scheduling/AppointmentsPanel";
import { StatusPill, formatDate, formatDateTime } from "../../../components/admin/scheduling/ui";
import WhatsAppMessageContent from "../../../components/admin/WhatsAppMessageContent";
import ChatAttachments from "../../../components/admin/ChatAttachments";
import NotificationStatus, { notificationOutcomeText } from "../../../components/admin/notifications/NotificationStatus";
import { FormWorkspace } from "./ChatbotFormsPage";
import FormPreview from "../../../components/admin/forms/FormPreview";
import CustomerAvatar from "../../../components/admin/scheduling/CustomerAvatar";
import CustomerPictureModal from "../../../components/admin/scheduling/CustomerPictureModal";
import useAuth from "../../../hooks/useAuth";
import CustomerImports from "../../../components/admin/scheduling/CustomerImports";

const selectClass = "w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-100 sm:w-64";
const phoneDigits = (value) => String(value || "").replace(/\D/g, "");
const appointmentTime = (value, timeZone) => new Date(value).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone });
const requestActivityTime = (request) => [
  request.created_at,
  request.sent_at,
  request.first_opened_at,
  request.last_opened_at,
  request.submitted_at,
  request.signed_at,
  request.expired_at,
  request.cancelled_at,
  request.updated_at,
].map((value) => new Date(value || 0).getTime()).filter(Number.isFinite).reduce((latest, value) => Math.max(latest, value), 0);

function AppointmentTimelineItem({ appointment, index, timeZone, providerNames, serviceNames, botId, providers, services, onSaved }) {
  return <li className="relative pb-6 last:pb-0">
    <span className="absolute -left-[1.63rem] top-1 flex h-5 w-5 items-center justify-center rounded-full border border-cyan-200 bg-cyan-50 text-cyan-800"><CalendarDays size={11} /></span>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="font-semibold text-slate-900">{formatDateTime(appointment.start_at, timeZone)}</p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Clock3 size={12} />{appointmentTime(appointment.start_at, timeZone)}–{appointmentTime(appointment.end_at, timeZone)}</p>
      </div>
      <StatusPill status={appointment.status} />
    </div>
    <div className="mt-2 space-y-1 text-sm text-slate-700">
      <p><span className="text-slate-500">Provider:</span> {providerNames.get(appointment.provider_id) || `#${appointment.provider_id}`}</p>
      <p><span className="text-slate-500">Service:</span> {appointment.service_id ? serviceNames.get(appointment.service_id) || `#${appointment.service_id}` : "Not specified"}</p>
      {appointment.notes && <p className="whitespace-pre-wrap"><span className="text-slate-500">Notes:</span> {appointment.notes}</p>}
      {appointment.cancel_reason && <p className="whitespace-pre-wrap text-rose-700"><span className="text-rose-600">Cancellation reason:</span> {appointment.cancel_reason}</p>}
      <p className="pt-1 text-xs text-slate-400">Visit {index + 1} · #{appointment.id} · Source: {appointment.source || "unknown"}</p>
      <NotificationStatus botId={botId} resourceType="appointment" resourceId={appointment.id} initial={appointment.whatsapp_notification || null} compact />
      <AppointmentActions appointment={appointment} botId={botId} timeZone={timeZone} providers={providers} services={services} onSaved={onSaved} />
    </div>
  </li>;
}

function WhatsAppTimelineItem({ message, botId }) {
  const outbound = message.direction === "outbound" || message.sender === "agent";
  const body = message.body || message.text || message.content || (message.msg_type ? `[${message.msg_type} message]` : "Message");
  const attachments = (message.attachments || []).map((attachment) => ({ ...attachment, bot_id: attachment.bot_id || botId }));
  return <li className={`flex pb-5 last:pb-0 ${outbound ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[90%] min-w-0 ${outbound ? "text-right" : "text-left"}`}>
      <p className="mb-1 text-xs text-slate-500">{outbound ? "Business" : "Customer"} · {message.created_at ? formatDateTime(message.created_at) : message.timestamp || "Time unavailable"}</p>
      <div className={`inline-block rounded-xl px-4 py-3 text-left text-sm leading-6 ${outbound ? "bg-emerald-50 text-slate-900" : "bg-slate-100 text-slate-900"}`}>
        <WhatsAppMessageContent content={body} />
        {!!attachments.length && <ChatAttachments attachments={attachments} />}
      </div>
    </div>
  </li>;
}

function RequestTimelineItem({ request, at, botId }) {
  const { can } = useAuth();
  const [preview, setPreview] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const canDownload = request.status === "signed";
  const isRegistration = request.source === "public_registration";
  const viewForm = async () => {
    setWorking(true);
    setError("");
    try {
      if (isRegistration && can("consent_requests.read_document")) {
        const result = await getFormDocument(botId, request.id, true);
        if (!result?.url) throw new Error("The signed registration form is unavailable.");
        window.open(result.url, "_blank", "noopener,noreferrer");
        return;
      }
      const result = await previewFormRequest(botId, request.id);
      if (!result?.content) throw new Error("This form's content is unavailable.");
      setPreview(result);
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not view this form request."));
    } finally {
      setWorking(false);
    }
  };
  const downloadForm = async () => {
    if (!canDownload || !can("consent_requests.read_document")) return;
    setWorking(true);
    setError("");
    try {
      const result = await getFormDocument(botId, request.id);
      if (!result?.url) throw new Error("The signed form download is unavailable.");
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not download the signed form."));
    } finally {
      setWorking(false);
    }
  };
  return <li className="relative pb-6 last:pb-0">
    <span className="absolute -left-[1.63rem] top-1 flex h-5 w-5 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-800"><FileText size={11} /></span>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="font-semibold text-slate-900">{request.content_snapshot?.template_name || `Form request #${request.id}`}</p>
        <p className="mt-0.5 text-xs text-slate-500">{formatDateTime(at)} · Request #{request.id}</p>
      </div>
      <StatusPill status={request.status} />
    </div>
    {request.status !== "draft" && <div className="mt-2"><NotificationStatus key={`${request.status}:${request.updated_at}`} botId={botId} resourceType="form_request" resourceId={request.id} compact /></div>}
    {request.submitted_at && <p className="mt-2 text-sm text-slate-700">Submitted {formatDateTime(request.submitted_at)}</p>}
    {request.cancel_reason && <p className="mt-1 text-sm text-rose-700">{request.cancel_reason}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      <button type="button" disabled={working} onClick={viewForm} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Eye size={15} />{isRegistration ? "View registration form" : "View form"}</button>
      {canDownload && can("consent_requests.read_document") && <button type="button" disabled={working} onClick={downloadForm} className="inline-flex items-center gap-2 rounded-lg border border-cyan-200 px-3 py-1.5 text-sm font-semibold text-cyan-800 hover:bg-cyan-50 disabled:opacity-50"><Download size={15} />Download signed PDF</button>}
    </div>
    {error && <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p>}
    {preview && <FormPreview preview={preview} onClose={() => setPreview(null)} />}
  </li>;
}

export default function CustomersPage() {
  const { can } = useAuth();
  const appointmentConfigRead = can("appointment_configuration.read");
  const appointmentLookupRead = can("booking_lookups.read") || appointmentConfigRead;
  const navigate = useNavigate();
  const workspace = useBotWorkspace();
  const { customerId: routeCustomerId } = useParams();
  const [bots, setBots] = useState([]);
  const [selectedBotId, setBotId] = useState("");
  const botId = workspace?.id || selectedBotId;
  const [timeZone, setTimeZone] = useState("UTC");
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [customerView, setCustomerView] = useState("card");
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const formsTab = requestedTab === "req-history" || requestedTab === "req-hostory" || requestedTab === "send-new-req";
  const pageView = formsTab && (can("consent_requests.read") || can("consent_requests.create"))
    ? "forms" : requestedTab === "customers" ? "customers"
      : can("appointments.read") ? "appointments" : "customers";
  const setTab = (tab) => setSearchParams((previous) => {
    const next = new URLSearchParams(previous);
    next.set("tab", tab);
    return next;
  });
  const setPageView = (view) => setTab(view === "forms"
    ? can("consent_requests.read") ? "req-history" : "send-new-req" : view);
  const customerPath = (customerId) => {
    const query = searchParams.toString();
    return `/agents/${encodeURIComponent(botId)}/customers/${encodeURIComponent(customerId)}${query ? `?${query}` : ""}`;
  };
  const [customerRefreshKey, setCustomerRefreshKey] = useState(0);
  const [showImports, setShowImports] = useState(false);
  const [history, setHistory] = useState(null);
  const [pictureCustomer, setPictureCustomer] = useState(null);
  const [bookingCustomer, setBookingCustomer] = useState(null);
  const [consentCustomer, setConsentCustomer] = useState(null);
  const [bookingNotice, setBookingNotice] = useState("");
  const [viewMode, setViewMode] = useState("history");
  const [chatMessages, setChatMessages] = useState([]);
  const [formRequests, setFormRequests] = useState([]);
  const [chatConversationId, setChatConversationId] = useState(null);
  const [chatCustomerId, setChatCustomerId] = useState(null);
  const [requestsCustomerId, setRequestsCustomerId] = useState(null);
  const [chatTotalMessages, setChatTotalMessages] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [whatsAppLoading, setWhatsAppLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [loadingBots, setLoadingBots] = useState(true);
  const [loadingBotData, setLoadingBotData] = useState(false);
  const [error, setError] = useState("");
  const historyRequestRef = useRef(0);
  const sendConsent = (customer) => {
    historyRequestRef.current += 1;
    setHistoryLoading(false);
    setConsentCustomer(customer);
  };

  useEffect(() => {
    if (workspace) {
      setBots([workspace]);
      setLoadingBots(false);
      return undefined;
    }
    let active = true;
    listBusinesses()
      .then((rows) => {
        if (!active) return;
        const activeBots = (rows || []).filter((bot) => bot.id && bot.deleted_at == null);
        setBots(activeBots);
        setBotId((current) => current || activeBots[0]?.id || "");
      })
      .catch((err) => active && setError(getSchedulingErrorMessage(err, "Could not load bots.")))
      .finally(() => active && setLoadingBots(false));
    return () => { active = false; };
  }, [workspace]);

  useEffect(() => {
    if (!botId) {
      setProviders([]);
      setServices([]);
      setTimeZone("UTC");
      return undefined;
    }
    let active = true;
    setLoadingBotData(true);
    setError("");
    Promise.all([
      appointmentConfigRead ? getSchedulingSettings(botId) : Promise.resolve({ timezone: workspace?.timezone || "UTC" }),
      appointmentLookupRead ? listProviders(botId) : Promise.resolve([]),
      appointmentLookupRead ? listServices(botId) : Promise.resolve([]),
    ])
      .then(([settings, providerRows, serviceRows]) => {
        if (!active) return;
        setTimeZone(settings?.timezone || "UTC");
        setProviders(providerRows || []);
        setServices(serviceRows || []);
      })
      .catch((err) => {
        if (!active) return;
        setTimeZone("UTC");
        setProviders([]);
        setServices([]);
        setError(getSchedulingErrorMessage(err, "Could not load scheduling details for this bot."));
      })
      .finally(() => active && setLoadingBotData(false));
    return () => { active = false; };
  }, [botId, appointmentConfigRead, appointmentLookupRead, workspace?.timezone]);

  useEffect(() => {
    historyRequestRef.current += 1;
    setHistory(null);
    setPictureCustomer(null);
    setViewMode("history");
    setBookingCustomer(null);
    setConsentCustomer(null);
    setBookingNotice("");
    setChatMessages([]);
    setFormRequests([]);
    setChatConversationId(null);
    setChatCustomerId(null);
    setRequestsCustomerId(null);
    setChatTotalMessages(null);
    setHistoryLoading(false);
    setWhatsAppLoading(false);
    setRequestsLoading(false);
    setHistoryError("");
  }, [botId]);

  const providerNames = useMemo(() => new Map(providers.map((provider) => [provider.id, provider.name])), [providers]);
  const serviceNames = useMemo(() => new Map(services.map((service) => [service.id, service.name])), [services]);
  const selectedBot = bots.find((bot) => bot.id === botId);

  const loadWhatsAppMessages = async (customer, requestId) => {
    if (!can("inbox.read")) return;
    if (!customer.phone) {
      setHistoryError("This customer has no phone number for WhatsApp lookup.");
      setChatMessages([]);
      setChatConversationId(null);
      setChatCustomerId(customer.id);
      return;
    }
    setWhatsAppLoading(true);
    setHistoryError("");
    setChatMessages([]);
    setChatConversationId(null);
    try {
      const phone = phoneDigits(customer.phone);
      const result = await fetchTenantWhatsappConversations(botId, {
        filter: "all", search: phone, active_only: false, page: 1, per_page: 100,
      });
      if (requestId !== historyRequestRef.current) return;
      const matches = (result.items || []).filter((conversation) => (
        phoneDigits(conversation.phone_number) === phone || phoneDigits(conversation.wa_id) === phone
      ));
      if (!matches.length) {
        setHistoryError("No WhatsApp conversation was found for this customer's phone number.");
        return;
      }
      const conversation = matches[0];
      const [messages, details] = await Promise.all([
        fetchTenantWhatsappMessages(botId, conversation.id, { limit: 200 }),
        fetchWhatsAppConversationDetails(botId, conversation.id),
      ]);
      if (requestId !== historyRequestRef.current) return;
      setChatConversationId(conversation.id);
      setChatCustomerId(customer.id);
      setChatTotalMessages(details?.total_messages ?? messages.length);
      setChatMessages(messages || []);
    } catch (err) {
      if (requestId === historyRequestRef.current) setHistoryError(getSchedulingErrorMessage(err, "Could not find this customer's WhatsApp conversation."));
    } finally {
      if (requestId === historyRequestRef.current) {
        setWhatsAppLoading(false);
      }
    }
  };

  const loadCustomerRequests = async (customer, requestId) => {
    if (!can("consent_requests.read")) return;
    setRequestsLoading(true);
    setHistoryError("");
    try {
      const rows = [];
      let offset = 0;
      while (true) {
        const page = await listFormRequests(botId, { customer_id: customer.id, limit: 100, offset });
        if (requestId !== historyRequestRef.current) return;
        const pageRows = Array.isArray(page) ? page : [];
        rows.push(...pageRows);
        if (pageRows.length < 100) break;
        offset += pageRows.length;
      }
      setFormRequests(rows);
      setRequestsCustomerId(customer.id);
    } catch (err) {
      if (requestId === historyRequestRef.current) setHistoryError(getSchedulingErrorMessage(err, "Could not load this customer's form requests."));
    } finally {
      if (requestId === historyRequestRef.current) setRequestsLoading(false);
    }
  };

  const loadHistory = async (customer, requestedView = "history") => {
    setConsentCustomer(null);
    const requestId = ++historyRequestRef.current;
    setViewMode(requestedView);
    setHistoryLoading(true);
    setHistoryError("");
    setHistory(null);
    setChatMessages([]);
    setFormRequests([]);
    setChatConversationId(null);
    setChatCustomerId(null);
    setRequestsCustomerId(null);
    setChatTotalMessages(null);
    try {
      const details = await getCustomer(botId, customer.id);
      if (requestId !== historyRequestRef.current) return;
      setHistory(details);
      const tasks = [];
      if (["chat", "all"].includes(requestedView)) tasks.push(loadWhatsAppMessages(details, requestId));
      tasks.push(loadCustomerRequests(details, requestId));
      await Promise.all(tasks);
    } catch (err) {
      if (requestId === historyRequestRef.current) setHistoryError(getSchedulingErrorMessage(err, "Could not load this patient's appointments."));
    } finally {
      if (requestId === historyRequestRef.current) setHistoryLoading(false);
    }
  };

  const refreshAppointments = () => {
    setCustomerRefreshKey((value) => value + 1);
    return loadHistory(history, viewMode);
  };

  const loadHistoryRef = useRef(loadHistory);
  loadHistoryRef.current = loadHistory;
  useEffect(() => {
    if (routeCustomerId && botId) loadHistoryRef.current({ id: routeCustomerId });
  }, [routeCustomerId, botId]);

  const changeView = (nextView) => {
    const requestId = ++historyRequestRef.current;
    setViewMode(nextView);
    if (!["chat", "all"].includes(nextView)) setWhatsAppLoading(false);
    if (!["request", "all"].includes(nextView)) setRequestsLoading(false);
    if (nextView === "history") {
      return;
    }
    if (!history) return;
    const tasks = [];
    if (["chat", "all"].includes(nextView) && chatCustomerId !== history.id) tasks.push(loadWhatsAppMessages(history, requestId));
    if (["request", "all"].includes(nextView) && requestsCustomerId !== history.id) tasks.push(loadCustomerRequests(history, requestId));
    Promise.all(tasks);
  };

  const appointments = [...(history?.appointments || [])].sort((first, second) => (
    new Date(first.start_at).getTime() - new Date(second.start_at).getTime()
  ));
  const chronologicalMessages = [...chatMessages].sort((first, second) => (
    new Date(first.created_at || first.timestamp || 0).getTime() - new Date(second.created_at || second.timestamp || 0).getTime()
  ));
  const chronologicalRequests = [...formRequests].sort((first, second) => requestActivityTime(first) - requestActivityTime(second));
  const combinedTimeline = [
    ...appointments.map((appointment) => ({ type: "appointment", at: new Date(appointment.start_at).getTime(), appointment })),
    ...chronologicalMessages.map((message) => ({ type: "message", at: new Date(message.created_at || 0).getTime(), message })),
    ...chronologicalRequests.map((request) => ({ type: "request", at: requestActivityTime(request), request })),
  ].sort((first, second) => second.at - first.at);

  return (
    <div className="w-full space-y-4">
      {showImports && botId && <CustomerImports key={botId} botId={botId} canCreate={can("customers.create")}
        onClose={() => setShowImports(false)} onImported={() => setCustomerRefreshKey((value) => value + 1)} />}
      <header className="border-b border-slate-200 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center border border-cyan-200 bg-cyan-50 text-cyan-800">
              <UserRound size={20} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-cyan-800">{selectedBot?.name || selectedBot?.slug || botId || "Customers"}</p>
              <h1 className="mt-0.5 text-2xl font-bold text-slate-950">Customers</h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div role="tablist" aria-label="Customer page views" className="flex flex-wrap gap-2">
              {[...(can("appointments.read") ? [["appointments", "Appointments"]] : []), ["customers", "Customers"],
                ...(can("consent_requests.read") || can("consent_requests.create") ? [["forms", "Forms"]] : [])].map(([key, label]) => <button type="button" key={key} role="tab" aria-selected={pageView === key} onClick={() => setPageView(key)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${pageView === key ? "bg-cyan-800 text-white" : "border border-slate-200 bg-white text-slate-700"}`}>{label}</button>)}
            </div>
            {workspace && routeCustomerId && can("prescriptions.create", selectedBot?.client_id) && <button
              type="button"
              onClick={() => navigate(`/agents/${encodeURIComponent(botId)}/prescriptions/${encodeURIComponent(routeCustomerId)}`)}
              className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-900 hover:bg-cyan-100"
            >Create prescription</button>}
            {!workspace && <div className="flex w-full items-center gap-2 sm:w-auto">
              <label htmlFor="customers-bot-select" className="shrink-0 text-sm font-semibold text-slate-700">Select bot</label>
              <select
                id="customers-bot-select"
                className={selectClass}
                value={botId}
                disabled={loadingBots || bots.length === 0}
                onChange={(event) => { setBotId(event.target.value); setHistory(null); setHistoryError(""); }}
              >
                {loadingBots && <option value="">Loading bots…</option>}
                {!loadingBots && bots.length === 0 && <option value="">No bots available</option>}
                {bots.map((bot) => <option key={bot.id} value={bot.id}>{bot.name || bot.slug || bot.id}</option>)}
              </select>
            </div>}
          </div>
        </div>
      </header>

      {error && <div role="alert" className="flex items-start gap-2 border-l-4 border-rose-600 bg-rose-50 px-4 py-3 text-sm text-rose-800"><AlertCircle size={18} className="mt-0.5 shrink-0" />{error}</div>}
      {loadingBots ? <p className="py-8 text-center text-sm text-slate-500">Loading bots…</p> : !bots.length ? (
        <p className="py-8 text-center text-sm text-slate-500">No bots are available for your account.</p>
      ) : loadingBotData ? (
        <p className="py-8 text-center text-sm text-slate-500">Loading {selectedBot?.name || "bot"} customers…</p>
      ) : botId ? (
        <section aria-label={`${selectedBot?.name || "Selected bot"} customers`}>
          {pageView === "forms" ? <FormWorkspace key={`forms:${botId}`} botId={botId} requestSections
            selectedRequestSection={requestedTab === "send-new-req" ? "send" : "history"}
            onRequestSectionChange={(section) => setTab(section === "send" ? "send-new-req" : "req-history")} /> : <>
          {bookingNotice && <div role="status" className="mb-3 flex items-start justify-between gap-2 border-l-4 border-emerald-600 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{bookingNotice}<button type="button" aria-label="Dismiss notice" onClick={() => setBookingNotice("")}><X size={14} /></button></div>}
          <ResizableCustomerLayout enabled={!!(pageView === "customers" || history || historyLoading || historyError || consentCustomer)}>
            <div className="min-w-0 overflow-x-auto">
            {pageView === "appointments" ? <AppointmentsPanel
              key={botId} botId={botId} timeZone={timeZone} providers={providers} services={services}
              appointmentView refreshKey={customerRefreshKey}
              onCustomer={(customer) => {
                if (workspace && String(customer.id) !== routeCustomerId) navigate(customerPath(customer.id));
                else loadHistory(customer);
              }}
            /> : <CustomersPanel
              key={botId}
              botId={botId}
              timeZone={timeZone}
              providers={providers}
              onHistory={(customer, requestedView) => {
                if (workspace && !requestedView && String(customer.id) !== routeCustomerId) navigate(customerPath(customer.id));
                else loadHistory(customer, requestedView);
              }}
              onAppointment={setBookingCustomer}
              onConsent={sendConsent}
              onImport={() => setShowImports(true)}
              viewMode={customerView}
              onViewModeChange={setCustomerView}
              fetchAll
              enableAppointmentFilter
              refreshKey={customerRefreshKey}
            />}

            {bookingCustomer && <NewAppointmentModal
              open
              customer={bookingCustomer}
              onClose={() => setBookingCustomer(null)}
              botId={botId}
              timeZone={timeZone}
              providers={providers}
              services={services}
              defaultDate={new Date().toLocaleDateString("en-CA", { timeZone })}
              onCreated={(created) => { const customer = bookingCustomer; setBookingNotice(created?.whatsapp_notification ? `Appointment booked. ${notificationOutcomeText(created.whatsapp_notification, "confirmation")}` : "Appointment booked."); setBookingCustomer(null); setCustomerRefreshKey((value) => value + 1); loadHistory(customer); }}
            />}
            </div>
            <aside hidden={pageView === "appointments" && !history && !historyLoading && !historyError && !consentCustomer} className="min-h-[34rem] min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white xl:sticky xl:top-20" aria-label="Patient appointments">
              {consentCustomer ? <div className="p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
                  <div><p className="text-xs font-semibold uppercase text-cyan-800">Patient record · #{consentCustomer.id}</p><h2 className="mt-1 text-xl font-bold text-slate-950">{consentCustomer.name || "Unnamed patient"}</h2></div>
                  <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={() => loadHistory(consentCustomer, "request")}>Back to patient record</button>
                </div>
                <FormWorkspace key={`${botId}:${consentCustomer.id}`} botId={botId} customer={consentCustomer} embedded consentOnly />
              </div> : historyLoading ? <div className="flex min-h-[34rem] items-center justify-center text-sm text-slate-500">Loading patient appointments…</div> : history ? <>
                <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-5">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase text-cyan-800">Patient record · #{history.id}</p>
                    <div className="relative mt-2 inline-block">
                      <CustomerAvatar customer={history} botId={botId} className="h-16 w-16" />
                      <button type="button" aria-label={`Update profile picture for ${history.name || "customer"}`}
                        onClick={() => setPictureCustomer(history)} className="absolute -bottom-1 -right-1 rounded-full border border-slate-200 bg-white p-1.5 text-slate-600 shadow-sm">
                        <Pencil size={13} />
                      </button>
                    </div>
                    <h2 className="mt-1 truncate text-xl font-bold text-slate-950">{history.name || "Unnamed patient"}</h2>
                    <p className="mt-1 text-sm text-slate-600">{history.phone || "No phone"}{history.email ? ` · ${history.email}` : ""}</p>
                    <p className="mt-1 text-xs text-slate-500">{history.registration_number ? `Registration: ${history.registration_number} · ` : ""}DOB: {history.date_of_birth ? formatDate(history.date_of_birth) : "Not recorded"}{history.dnd ? " · DND preference recorded" : ""}</p>
                    <p className="mt-1 text-xs text-slate-500">Customer since {history.created_at ? formatDateTime(history.created_at, timeZone) : "Unknown"}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button type="button" onClick={() => sendConsent(history)} aria-label="Send consent form" title="Send consent form" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-200 text-cyan-800 hover:bg-cyan-50"><FileSignature size={16} /></button>
                      <button type="button" onClick={() => setBookingCustomer(history)} aria-label="Book appointment" title="Book appointment" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-200 text-cyan-800 hover:bg-cyan-50"><CalendarPlus size={16} /></button>
                    </div>
                  </div>
                  <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={() => { setHistory(null); setHistoryError(""); }} aria-label="Close patient appointments"><X size={18} /></button>
                </header>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="group" aria-label="View">
                    {[...(can("appointments.read") ? [["history", "Appointment"]] : []),
                      ...(can("inbox.read") ? [["chat", "Chat"]] : []),
                      ...(can("consent_requests.read") ? [["request", "Request"]] : []),
                      ...(Number(can("appointments.read")) + Number(can("inbox.read")) + Number(can("consent_requests.read")) > 1 ? [["all", "All"]] : [])].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${viewMode === value ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white hover:text-slate-900"}`}
                        aria-pressed={viewMode === value}
                        onClick={() => changeView(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {chatConversationId && <button type="button" onClick={() => navigate(`/agents/${encodeURIComponent(botId)}/inbox/${encodeURIComponent(chatConversationId)}`)} className="inline-flex min-h-9 shrink-0 items-center gap-2 rounded-lg border border-emerald-200 px-3 py-1.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-50">
                    <ExternalLink size={15} /> Open full inbox
                  </button>}
                </div>
                {historyError && <div role="alert" className="mx-5 mt-4 border-l-4 border-rose-600 bg-rose-50 px-3 py-2 text-sm text-rose-800">{historyError}</div>}
                {!["request", "all"].includes(viewMode) && can("consent_requests.read") && <section aria-label="Signed registration forms" className="border-b border-slate-200 px-5 py-3">
                  {requestsLoading ? <p role="status" className="text-sm text-slate-500">Loading registration forms...</p>
                    : chronologicalRequests.some((request) => request.source === "public_registration" && request.status === "signed") && <>
                      <h3 className="mb-3 text-sm font-bold text-slate-900">Signed registration forms</h3>
                      <ol className="border-l border-slate-200 pl-5">{[...chronologicalRequests].reverse()
                        .filter((request) => request.source === "public_registration" && request.status === "signed")
                        .map((request) => <RequestTimelineItem key={request.id} botId={botId} request={request} at={requestActivityTime(request)} />)}</ol>
                    </>}
                </section>}
                <div className="max-h-[calc(100vh-22rem)] min-h-[20rem] overflow-y-auto p-5">
                  {whatsAppLoading ? <p className="py-8 text-center text-sm text-slate-500">Loading WhatsApp messages…</p> : null}
                  {requestsLoading && viewMode === "request" ? <p className="py-8 text-center text-sm text-slate-500">Loading form requests…</p> : null}
                  {!whatsAppLoading && viewMode === "history" && (appointments.length ? <ol className="relative space-y-0 border-l border-slate-200 pl-5">
                    {appointments.map((appointment, index) => <AppointmentTimelineItem key={`appointment-${appointment.id}`} appointment={appointment} index={index} timeZone={timeZone} providerNames={providerNames} serviceNames={serviceNames} botId={botId} providers={providers} services={services} onSaved={refreshAppointments} />)}
                  </ol> : <div className="flex min-h-[20rem] flex-col items-center justify-center text-center">
                    <CalendarDays size={24} className="text-slate-300" />
                    <p className="mt-2 text-sm font-semibold text-slate-700">No appointments recorded</p>
                    <p className="mt-1 text-xs text-slate-500">New visits will appear here in chronological order.</p>
                  </div>)}
                  {!whatsAppLoading && viewMode === "chat" && (chronologicalMessages.length ? <>
                    <p className="mb-4 text-xs text-slate-500">Showing {chronologicalMessages.length}{chatTotalMessages > chronologicalMessages.length ? ` of ${chatTotalMessages}` : ""} messages · oldest to newest</p>
                    <ol className="space-y-4">{chronologicalMessages.map((message) => <WhatsAppTimelineItem key={`message-${message.id}`} message={message} botId={botId} />)}</ol>
                  </> : <div className="flex min-h-[20rem] flex-col items-center justify-center text-center">
                    <MessageCircle size={24} className="text-slate-300" />
                    <p className="mt-2 text-sm font-semibold text-slate-700">No WhatsApp messages found</p>
                    <p className="mt-1 text-xs text-slate-500">This customer may not have a linked WhatsApp conversation.</p>
                  </div>)}
                  {!whatsAppLoading && !requestsLoading && viewMode === "request" && (chronologicalRequests.length ? <>
                    <p className="mb-4 text-xs text-slate-500">{chronologicalRequests.length} form requests · newest activity first</p>
                    <ol className="relative space-y-0 border-l border-slate-200 pl-5">{[...chronologicalRequests].reverse().map((request) => <RequestTimelineItem key={`request-${request.id}`} botId={botId} request={request} at={requestActivityTime(request)} />)}</ol>
                  </> : <div className="flex min-h-[20rem] flex-col items-center justify-center text-center">
                    <FileText size={24} className="text-slate-300" />
                    <p className="mt-2 text-sm font-semibold text-slate-700">No form requests found</p>
                  </div>)}
                  {!whatsAppLoading && !requestsLoading && viewMode === "all" && (combinedTimeline.length ? <>
                    <p className="mb-4 text-xs text-slate-500">Appointments, WhatsApp messages, and form requests · newest activity first{chatTotalMessages > chronologicalMessages.length ? ` · showing latest ${chronologicalMessages.length} of ${chatTotalMessages} messages` : ""}</p>
                    <ol className="space-y-2">{combinedTimeline.map((entry) => entry.type === "appointment" ? <AppointmentTimelineItem key={`appointment-${entry.appointment.id}`} appointment={entry.appointment} index={appointments.indexOf(entry.appointment)} timeZone={timeZone} providerNames={providerNames} serviceNames={serviceNames} botId={botId} providers={providers} services={services} onSaved={refreshAppointments} /> : entry.type === "request" ? <RequestTimelineItem key={`request-${entry.request.id}`} botId={botId} request={entry.request} at={entry.at} /> : <WhatsAppTimelineItem key={`message-${entry.message.id}`} message={entry.message} botId={botId} />)}</ol>
                  </> : <div className="flex min-h-[20rem] flex-col items-center justify-center text-center">
                    <CalendarDays size={24} className="text-slate-300" />
                    <p className="mt-2 text-sm font-semibold text-slate-700">No activity recorded</p>
                    <p className="mt-1 text-xs text-slate-500">Appointments, WhatsApp messages, and form requests will appear here.</p>
                  </div>)}
                </div>
              </> : <div className="flex min-h-[34rem] flex-col items-center justify-center px-6 text-center">
                <UsersRound size={30} className="text-slate-300" />
                <h2 className="mt-3 text-base font-semibold text-slate-800">Patient appointments</h2>
                <p className="mt-1 max-w-xs text-sm text-slate-500">Select View on a customer to review appointments, WhatsApp activity, and form requests.</p>
              </div>}
            </aside>
          </ResizableCustomerLayout>
          </>}
        </section>
      ) : null}
      {pictureCustomer && <CustomerPictureModal key={`${botId}:${pictureCustomer.id}`} customer={pictureCustomer} botId={botId}
          onClose={() => setPictureCustomer(null)} onSaved={(updated) => {
            setHistory((previous) => previous?.id === updated.id ? { ...previous,
              profile_picture_file_id: updated.profile_picture_file_id,
              profile_picture_url: updated.profile_picture_url,
              profile_picture_expires_at: updated.profile_picture_expires_at,
            } : previous);
            setCustomerRefreshKey((previous) => previous + 1);
          }} />}
    </div>
  );
}